import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Grid,
  Column,
  Tile,
  Tag,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  InlineNotification,
  InlineLoading,
  Accordion,
  AccordionItem,
} from '@carbon/react';
import { Chat, Send, Time, TrashCan } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSupportTickets } from '../hooks/useSupportTickets';
import {
  SUPPORT_TICKET_CATEGORIES,
  getStatusLabel,
  getStatusTagType,
} from '../services/supportService';
import { formatRelativeTime } from '../lib/timeUtils';

const SupportPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const { tickets, isLoading, error, create } = useSupportTickets();

  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submittedTicket, setSubmittedTicket] = useState(null);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmittedTicket(null);

    if (!isAuthenticated) {
      setSubmitError('You must be logged in to submit a support ticket.');
      navigate('/login', { state: { from: '/support' } });
      return;
    }

    const newErrors = {};
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const ticket = await create(formData);
      setSubmittedTicket(ticket);
      setFormData({ subject: '', category: 'general', description: '' });
      setErrors({});
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="support-page">
      <section style={{ backgroundColor: '#0f62fe', padding: '80px 0' }}>
        <Grid>
          <Column lg={10} md={6} sm={4}>
            <div style={{ color: '#ffffff' }}>
              <Tag type="blue" style={{ marginBottom: '16px' }}>Help Centre</Tag>
              <h1 style={{ fontSize: '3rem', fontWeight: 600, marginBottom: '24px' }}>
                Help Centre
              </h1>
              <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                Find answers, submit a request, and manage your ChillVerify account.
              </p>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Account Deletion — publicly visible, no login required */}
      <section style={{ padding: '64px 0 0' }} data-testid="account-deletion-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '32px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <TrashCan size={24} style={{ color: '#da1e28' }} />
                <h2 style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
                  How to Delete Your ChillVerify Account
                </h2>
              </div>
              <p style={{ color: textSecondary, marginBottom: '16px', lineHeight: 1.6 }}>
                ChillVerify lets you permanently delete your account and associated data from
                both the mobile app and this website at any time.
              </p>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                Option 1: Delete in-app or on the web
              </h3>
              <ol style={{ color: textSecondary, marginBottom: '20px', paddingLeft: '20px', lineHeight: 1.8 }}>
                <li>Log in to your ChillVerify account (web at chillverify.com or the mobile app).</li>
                <li>
                  On the web, go to{' '}
                  <Button
                    kind="ghost"
                    size="sm"
                    style={{ padding: '0 4px', minHeight: 'auto' }}
                    onClick={() => navigate('/dashboard/settings')}
                    data-testid="account-deletion-settings-link"
                  >
                    Dashboard &gt; Settings
                  </Button>
                  . On mobile, open the app menu and go to Settings.
                </li>
                <li>Scroll to the <strong>Danger Zone</strong> section and select <strong>Delete My Account</strong>.</li>
                <li>Confirm by typing "delete account" and submitting. This is permanent and cannot be undone.</li>
              </ol>
              <p style={{ color: textSecondary, marginBottom: '8px', fontSize: '0.875rem' }}>
                Deleting your account permanently removes:
              </p>
              <ul style={{ color: textSecondary, marginBottom: '20px', paddingLeft: '20px', lineHeight: 1.8, fontSize: '0.875rem' }}>
                <li>Your account and login credentials</li>
                <li>All sensors, gateways and device data</li>
                <li>All sensor readings and history</li>
                <li>Orders, subscriptions and alert rules</li>
              </ul>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                Option 2: Request deletion without app access
              </h3>
              <p style={{ color: textSecondary, marginBottom: 0, lineHeight: 1.6 }}>
                If you can't access the app, submit a ticket below with category{' '}
                <strong>"Account Deletion Request"</strong> (sign-in required to submit), or email{' '}
                <a href="mailto:support@chillverify.com" style={{ color: '#0f62fe' }}>support@chillverify.com</a>{' '}
                from your registered address. We will confirm your identity and delete your account and
                data within 30 days.
              </p>
            </Tile>
          </Column>
        </Grid>
      </section>

      <section style={{ padding: '80px 0' }}>
        <Grid>
          <Column lg={8} md={4} sm={4}>
            <h2 style={{ color: textPrimary, marginBottom: '8px', fontSize: '2rem', fontWeight: 600 }}>
              Submit a ticket
            </h2>
            <p style={{ color: textSecondary, marginBottom: '24px', lineHeight: 1.6 }}>
              {isAuthenticated
                ? `Signed in as ${user?.email}. We typically respond within one business day.`
                : 'Sign in to submit and track support tickets, including account deletion requests.'}
            </p>

            {!isAuthenticated && (
              <InlineNotification
                kind="warning"
                title="Authentication Required"
                subtitle="You must be logged in to submit a support ticket. Please sign in to continue."
                actionButtonLabel="Sign In"
                onActionButtonClick={() => navigate('/login', { state: { from: '/support' } })}
                style={{ marginBottom: '24px' }}
                data-testid="auth-required-notification"
              />
            )}

            {submittedTicket && (
              <InlineNotification
                kind="success"
                title="Ticket submitted"
                subtitle={`Reference ${submittedTicket.ticketNumber} — we'll update the status as we work on your request.`}
                onCloseButtonClick={() => setSubmittedTicket(null)}
                style={{ marginBottom: '24px' }}
                data-testid="ticket-submitted-notification"
              />
            )}

            {submitError && (
              <InlineNotification
                kind="error"
                title="Submission failed"
                subtitle={submitError}
                onCloseButtonClick={() => setSubmitError('')}
                style={{ marginBottom: '24px' }}
              />
            )}

            <Tile
              style={{
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                padding: '32px',
              }}
            >
              <form onSubmit={handleSubmit} data-testid="support-ticket-form">
                <TextInput
                  id="subject"
                  name="subject"
                  labelText="Subject"
                  placeholder="Brief summary of your issue"
                  value={formData.subject}
                  onChange={handleChange}
                  invalid={!!errors.subject}
                  invalidText={errors.subject}
                  disabled={!isAuthenticated}
                  style={{ marginBottom: '16px' }}
                  data-testid="ticket-subject"
                />

                <Select
                  id="category"
                  name="category"
                  labelText="Category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={!isAuthenticated}
                  style={{ marginBottom: '16px' }}
                  data-testid="ticket-category"
                >
                  {SUPPORT_TICKET_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} text={cat.label} />
                  ))}
                </Select>

                <TextArea
                  id="description"
                  name="description"
                  labelText="Description"
                  placeholder="Describe the issue, steps to reproduce, and any relevant device or zone names"
                  value={formData.description}
                  onChange={handleChange}
                  rows={6}
                  invalid={!!errors.description}
                  invalidText={errors.description}
                  disabled={!isAuthenticated}
                  style={{ marginBottom: '24px' }}
                  data-testid="ticket-description"
                />

                <Button
                  type="submit"
                  kind="primary"
                  renderIcon={Send}
                  disabled={!isAuthenticated || isSubmitting}
                  data-testid="ticket-submit"
                >
                  {isSubmitting ? 'Submitting...' : isAuthenticated ? 'Submit ticket' : 'Sign In to Submit'}
                </Button>
              </form>
            </Tile>
          </Column>

          <Column lg={8} md={4} sm={4}>
            <h2 style={{ color: textPrimary, marginBottom: '8px', fontSize: '2rem', fontWeight: 600 }}>
              Your tickets
            </h2>
            <p style={{ color: textSecondary, marginBottom: '24px', lineHeight: 1.6 }}>
              Track status updates for requests you've submitted.
            </p>

            {error && (
              <InlineNotification
                kind="error"
                title="Unable to load tickets"
                subtitle={error}
                style={{ marginBottom: '24px' }}
              />
            )}

            {isLoading ? (
              <InlineLoading description="Loading tickets..." />
            ) : tickets.length === 0 ? (
              <Tile
                style={{
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                  padding: '32px',
                  textAlign: 'center',
                }}
                data-testid="no-tickets"
              >
                <Chat size={32} style={{ color: '#0f62fe', marginBottom: '16px' }} />
                <p style={{ color: textPrimary, fontWeight: 600, marginBottom: '8px' }}>No tickets yet</p>
                <p style={{ color: textSecondary, margin: 0, fontSize: '0.875rem' }}>
                  Submit a ticket on the left and it will appear here with live status.
                </p>
              </Tile>
            ) : (
              <Accordion data-testid="support-tickets-list">
                {tickets.map((ticket) => (
                  <AccordionItem
                    key={ticket.id}
                    title={
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', width: '100%' }}>
                        <span style={{ color: textPrimary, fontWeight: 600 }}>{ticket.subject}</span>
                        <Tag type={getStatusTagType(ticket.status)} size="sm">
                          {getStatusLabel(ticket.status)}
                        </Tag>
                        <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                          {ticket.ticketNumber}
                        </span>
                      </div>
                    }
                    data-testid={`ticket-item-${ticket.id}`}
                  >
                    <div style={{ padding: '8px 0 16px' }}>
                      <p style={{ color: textSecondary, fontSize: '0.8125rem', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Time size={14} />
                        Opened {formatRelativeTime(ticket.createdAt)}
                        {ticket.updatedAt !== ticket.createdAt && (
                          <> · Updated {formatRelativeTime(ticket.updatedAt)}</>
                        )}
                      </p>
                      <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
                        {ticket.description}
                      </p>
                      {ticket.adminResponse ? (
                        <div
                          style={{
                            backgroundColor: bgPrimary,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '4px',
                            padding: '16px',
                          }}
                          data-testid={`ticket-response-${ticket.id}`}
                        >
                          <p style={{ color: textPrimary, fontWeight: 600, marginBottom: '8px', fontSize: '0.875rem' }}>
                            Response from ChillVerify support
                          </p>
                          <p style={{ color: textSecondary, margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                            {ticket.adminResponse}
                          </p>
                        </div>
                      ) : ticket.status !== 'resolved' ? (
                        <p style={{ color: textSecondary, fontSize: '0.8125rem', margin: 0, fontStyle: 'italic' }}>
                          Our team will respond here when your ticket is updated.
                        </p>
                      ) : null}
                    </div>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </Column>
        </Grid>
      </section>
    </div>
  );
};

export default SupportPage;
